import React, { useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSearch } from "../../../../contexts/SearchContext";
import { useBookingFlow } from "../hooks/useBookingFlow";
import { useFlightRealProposalSubmit } from "../context/FlightRealProposalSubmitContext";
import { useFlightOfferUi } from "../../../../contexts/FlightOfferUiContext";
import { FLIGHTS_PROPOSAL_PRIMARY_CTA_HE } from "../../../../utils/flightsNewLdsUi";
import { buildPricedSelfComposeCombinedOffer } from "../selfCompose/selfComposeSummaryUtils";
import { buildFlightFlowDashboardPath } from "../../flightBookingPathUtils";
import { applyResolvedFlightWizardBack } from "./flightWizardBackStep";
import { isSelfComposeContinueEnabled } from "./flightWizardStepResolve";

export default function FlightWizardNavigation() {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const routeParams = useParams();
  const { setFlightSearchEditorOpen } = useFlightOfferUi();
  const {
    currentStep,
    goToStep,
    nextStep,
    canAdvanceFromCurrentStep,
    searchGate,
    selectedFlightOffer,
    selectFlightOfferAndNextStep,
    holdResult,
  } = useBookingFlow();
  const {
    selectedSelfComposeOutboundDeal,
    selectedSelfComposeInboundDeal,
    roomInfo,
    setSavedDeal,
    calculateLDSDealFareForRoomsComposition,
  } = useSearch();
  const {
    useRealFlow,
    submitProposalFromReview,
    isSubmittingProposal,
  } = useFlightRealProposalSubmit();

  const atTerminal = currentStep === "success";
  const selfComposeBothLegsReady = isSelfComposeContinueEnabled(
    selectedSelfComposeOutboundDeal,
    selectedSelfComposeInboundDeal
  );
  const canNext =
    currentStep === "selfComposePick"
      ? selfComposeBothLegsReady
      : canAdvanceFromCurrentStep();
  const nextLabel =
    currentStep === "review"
      ? FLIGHTS_PROPOSAL_PRIMARY_CTA_HE
      : currentStep === "extras"
        ? "המשך לסיכום ההזמנה"
        : currentStep === "results" && searchGate?.outboundOnlyNoReturn
          ? "המשך ללא חזור"
          : "המשך";
  // One continue on this footer. Do not hide it on flightDetails: PathSync
  // often leaves /flights/results while this step is showing, which hid the sidebar CTA.
  const showNext =
    !atTerminal &&
    currentStep !== "draftPayment" &&
    currentStep !== "holdConfirmation" &&
    currentStep !== "review";

  const handleBackToCategories = () => {
    const targetPath = "/p/dashboard/searchoffer";
    if (currentStep !== "search") {
      goToStep("search");
    }
    setFlightSearchEditorOpen(false);
    if (routerLocation.pathname === targetPath) return;
    navigate(targetPath, { replace: true });
  };

  /**
   * From flightDetails / selfComposePick: stay in the flight flow.
   * history.back() (and the old goToStep("search") + /searchoffer root) can
   * remount the empty search landing because PathSync uses replace and
   * FlightSearchToResultsBridge will not re-open results without a deals
   * transition.
   */
  const handleBack = () => {
    if (currentStep === "draftPayment" || currentStep === "holdConfirmation") {
      const back =
        currentStep === "holdConfirmation"
          ? "review"
          : holdResult
            ? "holdConfirmation"
            : "review";
      setFlightSearchEditorOpen(false);
      goToStep(back);
      const targetPath = buildFlightFlowDashboardPath(routeParams, back);
      if (routerLocation.pathname !== targetPath) {
        navigate(targetPath);
      }
      return;
    }
    const backStep = applyResolvedFlightWizardBack({
      currentStep,
      selectedFlightOffer,
      searchGate,
      goToStep,
      navigate,
      pathname: routerLocation.pathname,
      routeParams,
      setFlightSearchEditorOpen,
    });
    if (backStep) return;
    handleBackToCategories();
  };

  const handleSelfComposeContinue = useCallback(() => {
    if (!selectedSelfComposeOutboundDeal || !selectedSelfComposeInboundDeal) {
      return;
    }
    const withSsot = buildPricedSelfComposeCombinedOffer(
      selectedSelfComposeOutboundDeal,
      selectedSelfComposeInboundDeal,
      roomInfo,
      calculateLDSDealFareForRoomsComposition
    );
    if (!withSsot?.rawDeal) {
      console.warn(
        "[FlightBookingMockup] buildPricedSelfComposeCombinedOffer failed"
      );
      return;
    }
    setSavedDeal(withSsot.rawDeal);
    selectFlightOfferAndNextStep(withSsot);
  }, [
    calculateLDSDealFareForRoomsComposition,
    roomInfo,
    selectFlightOfferAndNextStep,
    setSavedDeal,
    selectedSelfComposeInboundDeal,
    selectedSelfComposeOutboundDeal,
  ]);

  return (
    <footer className="fw-nav">
      <button
        type="button"
        className="fw-btn fw-btn--ghost"
        disabled={false}
        onClick={handleBack}
      >
        חזור
      </button>
      <div className="fw-nav__hint">
        {currentStep === "draftPayment" ? (
          <span className="fw-nav__hint-text">
            השתמשו בכרטיסים למעלה לשמירת טיוטה או לאישור הזמנה
          </span>
        ) : null}
      </div>
      {showNext ? (
        <button
          type="button"
          className="fw-btn fw-btn--primary"
          disabled={
            !canNext ||
            (currentStep === "review" && useRealFlow && isSubmittingProposal)
          }
          onClick={() => {
            if (currentStep === "review" && useRealFlow) {
              submitProposalFromReview();
              return;
            }
            if (
              currentStep === "selfComposePick" ||
              (selfComposeBothLegsReady &&
                !selectedFlightOffer?.isSelfComposeCombined &&
                selectedFlightOffer?.source !== "selfCompose" &&
                currentStep !== "review" &&
                currentStep !== "extras")
            ) {
              handleSelfComposeContinue();
              return;
            }
            nextStep();
          }}
        >
          {currentStep === "review" && useRealFlow && isSubmittingProposal
            ? "מעביר הזמנה לספק…"
            : nextLabel}
        </button>
      ) : (
        <span className="fw-nav__spacer" aria-hidden />
      )}
    </footer>
  );
}
